import { Alert, Box } from '@mui/material'
import React, { Component, useEffect, useMemo, useState, type ErrorInfo, type ReactNode } from 'react'
import type { AssetRenderComponentProps } from '../assetTypes'
import compileFeatureComponent from './compileFeatureComponent'

type FeatureRenderBoundaryProps = {
  children: ReactNode
  resetKey: string
  onError: (error: Error, errorInfo: ErrorInfo) => void
}

type FeatureRenderBoundaryState = {
  error?: Error
}

class FeatureRenderBoundary extends Component<FeatureRenderBoundaryProps, FeatureRenderBoundaryState> {
  state: FeatureRenderBoundaryState = {}

  static getDerivedStateFromError(error: Error): FeatureRenderBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError(error, errorInfo)
  }

  componentDidUpdate(previousProps: FeatureRenderBoundaryProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: undefined })
    }
  }

  render() {
    if (this.state.error) {
      return <Alert severity="error">{this.state.error.message}</Alert>
    }
    return this.props.children
  }
}

const FeatureAssetRenderer = ({ content, trellis, renderData, onLog }: AssetRenderComponentProps) => {
  const [runtimeError, setRuntimeError] = useState<string | null>(null)

  const compilation = useMemo(() => {
    try {
      return { component: compileFeatureComponent(content), error: null }
    } catch (error) {
      return { component: null, error: error instanceof Error ? error.message : String(error) }
    }
  }, [content])

  useEffect(() => {
    setRuntimeError(null)
  }, [content])

  useEffect(() => {
    if (!compilation.error) return
    onLog?.(`Feature compile error: ${compilation.error}`)
  }, [compilation.error, onLog])

  if (!trellis) {
    return <Alert severity="error">Feature runtime bridge is unavailable.</Alert>
  }

  if (compilation.error || !compilation.component) {
    return <Alert severity="error">{compilation.error ?? 'Feature payload could not be compiled.'}</Alert>
  }

  const RenderedFeature = compilation.component

  return (
    <FeatureRenderBoundary
      resetKey={content}
      onError={(error) => {
        setRuntimeError(error.message)
        onLog?.(`Feature render error: ${error.message}`)
      }}
    >
      <Box sx={{ height: '100%', width: '100%' }}>
        {runtimeError ? (
          <Alert severity="error">{runtimeError}</Alert>
        ) : (
          <RenderedFeature trellis={trellis} renderData={renderData} content={content} />
        )}
      </Box>
    </FeatureRenderBoundary>
  )
}

export default FeatureAssetRenderer
